import { useCallback, useSyncExternalStore } from 'react';

import { VERSION_PREFERENCE, VERSION_PREFERENCE_STORAGE_KEY } from './constants';
import type {
    StoredVersionPreference,
    UseVersionPreferenceResult,
    VersionPreference
} from './types';

const isVersionPreference = (value: unknown): value is VersionPreference =>
    value === VERSION_PREFERENCE.V1 || value === VERSION_PREFERENCE.V2;

const readStoredPreference = (): VersionPreference | null => {
    try {
        const raw = localStorage.getItem(VERSION_PREFERENCE_STORAGE_KEY);

        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as Partial<StoredVersionPreference>;

        return isVersionPreference(parsed.preference) ? parsed.preference : null;
    } catch {
        return null;
    }
};

const subscribers = new Set<() => void>();

const notifySubscribers = () => {
    subscribers.forEach(notify => notify());
};

const subscribe = (notify: () => void) => {
    subscribers.add(notify);

    const handleStorage = (event: StorageEvent) => {
        if (event.key === VERSION_PREFERENCE_STORAGE_KEY) {
            notify();
        }
    };

    window.addEventListener('storage', handleStorage);

    return () => {
        subscribers.delete(notify);
        window.removeEventListener('storage', handleStorage);
    };
};

const writeStoredPreference = (preference: VersionPreference) => {
    try {
        localStorage.setItem(VERSION_PREFERENCE_STORAGE_KEY, JSON.stringify({ preference }));
        notifySubscribers();
    } catch {
        // storage unavailable — leave subscribers unaware; nothing changed
    }
};

const useVersionPreference = (): UseVersionPreferenceResult => {
    const preference = useSyncExternalStore(subscribe, readStoredPreference, () => null);

    const setPreferenceV1 = useCallback(() => {
        writeStoredPreference(VERSION_PREFERENCE.V1);
    }, []);

    const setPreferenceV2 = useCallback(() => {
        writeStoredPreference(VERSION_PREFERENCE.V2);
    }, []);

    return {
        preference,
        setPreferenceV1,
        setPreferenceV2
    };
};

export default useVersionPreference;
